import { useCallback, useEffect, useState } from 'react';

import {
  fetchAndCacheAllBriefs,
  fetchBrief,
  fetchConversationBriefs,
  publishBrief,
} from '../atlasApi';
import { briefsToVersionFormat, buildShareUrl } from '../chat/helpers';

export const BRIEF_MAX_WIDTH = 1200;

export function useAtlasBriefs({
  isMobile,
  briefFullscreenMobile,
  setBriefFullscreenMobile,
  conversationIdRef,
  readOwnerUserIdRef,
  briefCacheRef,
  chatScope,
  clientWriteEnabled,
  dataUserId,
  workspaceContext,
}) {
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefFullscreen, setBriefFullscreen] = useState(false);
  const [briefWidth, setBriefWidth] = useState(() => {
    const raw = window.localStorage.getItem('atlas_brief_width_px');
    const availableWidth = window.innerWidth - 280;
    const defaultWidth = Math.floor(availableWidth / 2);
    const width = raw ? Number(raw) : defaultWidth;
    if (!Number.isFinite(width)) return defaultWidth;
    return Math.max(320, Math.min(BRIEF_MAX_WIDTH, width));
  });
  const [activeBriefId, setActiveBriefId] = useState(null);
  const [activeBrief, setActiveBrief] = useState(null);
  const [briefShareUrl, setBriefShareUrl] = useState('');
  const [briefPublishError, setBriefPublishError] = useState('');
  const [briefPublishing, setBriefPublishing] = useState(false);
  const [briefVersions, setBriefVersions] = useState([]);
  const [currentVersionNumber, setCurrentVersionNumber] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.localStorage.setItem('atlas_brief_width_px', String(briefWidth));
    return undefined;
  }, [briefWidth]);

  useEffect(() => {
    if (briefFullscreenMobile == null) return;

    if (isMobile && briefFullscreen) {
      setBriefFullscreen(false);
      if (briefOpen) setBriefFullscreenMobile?.(true);
    } else if (!isMobile && briefFullscreenMobile) {
      setBriefFullscreenMobile?.(false);
      if (briefOpen) setBriefFullscreen(true);
    }
  }, [briefFullscreen, briefFullscreenMobile, briefOpen, isMobile, setBriefFullscreenMobile]);

  const resetBriefState = useCallback(({ clearCache = false } = {}) => {
    setActiveBriefId(null);
    setActiveBrief(null);
    setBriefShareUrl('');
    setBriefPublishError('');
    setBriefPublishing(false);
    setBriefVersions([]);
    setCurrentVersionNumber(null);
    setBriefOpen(false);
    setBriefFullscreen(false);
    if (clearCache) {
      briefCacheRef.current.clear();
    }
  }, [briefCacheRef]);

  const openBriefById = useCallback(async (briefId) => {
    if (!briefId) return;
    setBriefFullscreen(false);
    setActiveBriefId(briefId);
    setBriefPublishError('');
    setBriefShareUrl('');

    const cachedBrief = briefCacheRef.current.get(briefId);
    if (cachedBrief) {
      setActiveBrief(cachedBrief);
      setBriefOpen(true);
      if (briefVersions.length > 0) {
        const currentIdx = briefVersions.findIndex((version) => version.brief_id === briefId);
        if (currentIdx >= 0) setCurrentVersionNumber(briefVersions[currentIdx].version_number);
      }
      return;
    }

    setBriefVersions([]);
    setCurrentVersionNumber(null);
    const cid = conversationIdRef.current;
    if (!cid) return;

    try {
      const ownerUserId = readOwnerUserIdRef?.current;
      const { briefs } = await fetchAndCacheAllBriefs({
        conversationId: cid,
        ownerUserId,
        cacheRef: briefCacheRef,
        workspaceContext,
      });

      let briefData = briefCacheRef.current.get(briefId);
      if (!briefData) {
        const briefRes = await fetchBrief({ conversationId: cid, briefId, ownerUserId, workspaceContext });
        if (briefRes?.brief) {
          briefData = briefRes.brief;
          briefCacheRef.current.set(briefId, briefData);
        }
      }

      if (briefData) {
        setActiveBrief(briefData);
        setBriefOpen(true);
      }

      const { versions, currentVersionNumber: versionNumber } = briefsToVersionFormat(briefs, briefId);
      setBriefVersions(versions);
      if (versionNumber) setCurrentVersionNumber(versionNumber);
    } catch {
      // ignore
    }
  }, [briefCacheRef, briefVersions, conversationIdRef, readOwnerUserIdRef, workspaceContext]);

  const handleSelectVersion = useCallback(async (selectedBriefId) => {
    if (!selectedBriefId) return;
    const cid = conversationIdRef.current;
    if (!cid) return;

    const cachedBrief = briefCacheRef.current.get(selectedBriefId);
    if (cachedBrief) {
      setActiveBriefId(selectedBriefId);
      setActiveBrief(cachedBrief);
      const currentIdx = briefVersions.findIndex((version) => version.brief_id === selectedBriefId);
      if (currentIdx >= 0) setCurrentVersionNumber(briefVersions[currentIdx].version_number);
      return;
    }

    try {
      const ownerUserId = readOwnerUserIdRef?.current;
      const res = await fetchBrief({
        conversationId: cid,
        briefId: selectedBriefId,
        ownerUserId,
        workspaceContext,
      });
      if (res?.brief) {
        briefCacheRef.current.set(selectedBriefId, res.brief);
        setActiveBriefId(selectedBriefId);
        setActiveBrief(res.brief);
        const currentIdx = briefVersions.findIndex((version) => version.brief_id === selectedBriefId);
        if (currentIdx >= 0) setCurrentVersionNumber(briefVersions[currentIdx].version_number);
      }
    } catch {
      // ignore
    }
  }, [briefCacheRef, briefVersions, conversationIdRef, readOwnerUserIdRef, workspaceContext]);

  const handleBriefClose = useCallback(() => {
    setBriefOpen(false);
    setBriefFullscreen(false);
  }, []);

  const handleBriefToggleFullscreen = useCallback(() => {
    setBriefFullscreen((value) => !value);
    setBriefOpen(true);
  }, []);

  const handleBriefPublish = useCallback(async () => {
    const cid = conversationIdRef.current;
    if (!cid || !activeBriefId) return;

    setBriefPublishing(true);
    setBriefPublishError('');
    try {
      const actAsUserId = chatScope === 'client' && clientWriteEnabled && dataUserId != null ? dataUserId : null;
      const ownerUserId = readOwnerUserIdRef?.current;
      const res = await publishBrief({ conversationId: cid, briefId: activeBriefId, actAsUserId, workspaceContext });
      const shareId = res?.published?.share_id;
      if (shareId) {
        setBriefShareUrl(buildShareUrl(shareId));
        try {
          const allBriefsRes = await fetchConversationBriefs({ conversationId: cid, ownerUserId, workspaceContext });
          const { versions, currentVersionNumber: versionNumber } = briefsToVersionFormat(allBriefsRes?.briefs, activeBriefId);
          setBriefVersions(versions);
          if (versionNumber) setCurrentVersionNumber(versionNumber);
        } catch {
          // ignore
        }
      }
    } catch (err) {
      setBriefPublishError(err?.message || String(err));
    } finally {
      setBriefPublishing(false);
    }
  }, [activeBriefId, chatScope, clientWriteEnabled, conversationIdRef, dataUserId, readOwnerUserIdRef, workspaceContext]);

  const handleOpenBriefMobile = useCallback((briefId) => {
    openBriefById(briefId);
    if (isMobile) {
      setBriefFullscreenMobile?.(true);
    }
  }, [isMobile, openBriefById, setBriefFullscreenMobile]);

  const handleBackFromBrief = useCallback(() => {
    setBriefFullscreenMobile?.(false);
    setBriefOpen(false);
  }, [setBriefFullscreenMobile]);

  return {
    BRIEF_MAX_WIDTH,
    briefOpen,
    setBriefOpen,
    briefFullscreen,
    setBriefFullscreen,
    briefWidth,
    setBriefWidth,
    activeBriefId,
    setActiveBriefId,
    activeBrief,
    setActiveBrief,
    briefShareUrl,
    setBriefShareUrl,
    briefPublishError,
    setBriefPublishError,
    briefPublishing,
    setBriefPublishing,
    briefVersions,
    setBriefVersions,
    currentVersionNumber,
    setCurrentVersionNumber,
    resetBriefState,
    handleBriefClose,
    openBriefById,
    handleSelectVersion,
    handleBriefToggleFullscreen,
    handleBriefPublish,
    handleOpenBriefMobile,
    handleBackFromBrief,
    showMobileBriefFullscreen: isMobile && briefFullscreenMobile && briefOpen,
  };
}
